import { Link, useLocation } from "react-router-dom";
import { format, isSameDay, isToday, isTomorrow, isYesterday } from "date-fns";
import type { PublicEvent } from "@vedaunion/shared";
import { modalLinkState } from "../../lib/modal-routing";

/**
 * Chronological list of upcoming events. Used in dashboard's "Agenda" tab and
 * in compact widget contexts. Groups by day with a sticky-style date header
 * so long lists remain navigable. Distinct from `EventList` (the rich landing
 * view) — Agenda runs leaner with grotesk typography to fit the platform
 * register.
 */

export function AgendaView({
  events,
  basePath = "/calendar",
  onCreateAt,
  emptyState = "Nothing scheduled in this range.",
}: {
  events: PublicEvent[];
  basePath?: string;
  onCreateAt?: (when: Date) => void;
  emptyState?: React.ReactNode;
}) {
  const location = useLocation();
  if (events.length === 0) {
    return (
      <div className="agenda-empty">
        <p className="font-grotesk text-[0.85rem] text-[var(--color-ink-mute)]">{emptyState}</p>
        {onCreateAt && (
          <button type="button" className="btn-app mt-3" onClick={() => onCreateAt(new Date())}>
            Add an event
          </button>
        )}
      </div>
    );
  }

  // Group by day for the headers.
  const groups: { day: Date; items: PublicEvent[] }[] = [];
  for (const ev of events) {
    const day = new Date(ev.startsAt);
    const last = groups[groups.length - 1];
    if (last && isSameDay(last.day, day)) last.items.push(ev);
    else groups.push({ day, items: [ev] });
  }

  return (
    <ul className="agenda-list">
      {groups.map((g, gi) => (
        <li key={gi} className="agenda-group">
          <h3 className="agenda-day">
            <span className="agenda-day-label">{relativeDay(g.day)}</span>
            <span className="agenda-day-detail">{format(g.day, "EEEE, d LLLL yyyy")}</span>
          </h3>
          <ul className="agenda-items">
            {g.items.map((ev) => (
              <li key={ev.occurrenceId}>
                <Link
                  to={`${basePath}/${ev.slug}`}
                  state={modalLinkState(location)}
                  className={`agenda-item ${ev.status === "cancelled" ? "is-cancelled" : ""}`}
                  data-cat={ev.category}
                >
                  <span className="agenda-time">
                    {ev.allDay ? "All day" : ev.timeTbd ? "Time TBA" : format(new Date(ev.startsAt), "HH:mm")}
                  </span>
                  <div className="agenda-body">
                    <p className="agenda-title">{ev.title}</p>
                    <p className="agenda-meta">
                      <span>{ev.category.replace(/-/g, " ")}</span>
                      {ev.locationType !== "in-person" && (
                        <>
                          <span className="agenda-meta-sep">·</span>
                          <span>{ev.locationType}</span>
                        </>
                      )}
                      {ev.location && (
                        <>
                          <span className="agenda-meta-sep">·</span>
                          <span>{ev.location}</span>
                        </>
                      )}
                      {ev.capacity !== null && (
                        <>
                          <span className="agenda-meta-sep">·</span>
                          <span>
                            {ev.rsvpCount}
                            {ev.capacity ? `/${ev.capacity}` : ""} going
                          </span>
                        </>
                      )}
                    </p>
                    {ev.summary && <p className="agenda-summary">{ev.summary}</p>}
                  </div>
                  {ev.myRsvp && <span className={`agenda-rsvp is-${ev.myRsvp}`}>{ev.myRsvp}</span>}
                </Link>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}

function relativeDay(d: Date): string {
  if (isToday(d)) return "Today";
  if (isTomorrow(d)) return "Tomorrow";
  if (isYesterday(d)) return "Yesterday";
  return format(d, "EEE");
}
